import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useDataTable, type DataTableButtonAction, type DataTableFilter } from '@odo/shared-ui';
import {
    useCommunications,
    useDeleteCommunication,
    useInfiniteCommunications
} from '../../hooks/useCommunications';
import { useCommunicationsSummary } from '../../hooks/useCommunicationsSummary';
import { usePracticeOptions } from '../../hooks/useBilling';
import { useAssigneeFilter } from '../../hooks/useAssigneeFilter';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useViewportTableLimit } from '../../hooks/useViewportTableLimit';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import { practiceOptionsFromList } from '../../utils/practiceFilterOptions';
import { ASSIGNEE_FILTER_ME, ASSIGNEE_FILTER_UNASSIGNED } from '../../utils/assigneeFilter';
import {
    UNASSIGNED_ASSIGNEE_FILTER,
    UNASSIGNED_PRACTICE_FILTER,
    type Communication,
    type CommunicationChannel,
    type CommunicationStatus
} from '../../services/communications';
import { CommunicationsMoreFiltersMenu } from '../../components/accounts/communications/CommunicationsMoreFiltersMenu';
import {
    CommunicationsStatCards,
    type CommunicationStat
} from '../../components/accounts/communications/CommunicationsStatCards';
import {
    COMMUNICATION_ROW_HEIGHT,
    CommunicationsTable
} from '../../components/accounts/practice/communications/CommunicationsTable';
import type { CommunicationsSortBy } from '../../components/accounts/practice/communications/communicationColumns';
import { DeleteCommunicationConfirmModal } from '../../components/accounts/practice/communications/DeleteCommunicationConfirmModal';

// Cross-practice counterpart to the practice-scoped Communications tab -
// same table/row/delete-modal, plus the status-card strip and filters.

const SEARCH_DEBOUNCE_MS = 300;
const ANY_PRACTICE = '';
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

// A type alias, not an interface: only aliases pick up TypeScript's implicit
// index signature, which is what lets this satisfy `DataTableFilterMap`.
type CommunicationFilters = {
    status: string[];
    channel: string[];
    category: string | null;
    practice: string;
    assignee: string[];
};

export function CommunicationsList() {
    usePageTitle('Communications');
    const { setTitle, setSubtitle } = usePageHeader();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { practices } = usePracticeOptions();

    const table = useDataTable<CommunicationsSortBy, CommunicationFilters>({
        defaultSort: { by: 'lastActivityAt', order: 'desc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: {
            status: [],
            channel: [],
            category: null,
            practice: ANY_PRACTICE,
            assignee: []
        }
    });
    const {
        status: statusFilter,
        channel: channelFilter,
        category: categoryFilter,
        practice: practiceFilter,
        assignee: assigneeFilter
    } = table.filters;
    const isInfinite = table.mode === 'infinite';
    const { options: assigneeOptions, apiValues: apiAssignees } = useAssigneeFilter(
        assigneeFilter,
        UNASSIGNED_ASSIGNEE_FILTER
    );

    const [search, setSearch] = useState('');
    const [deletingCommunication, setDeletingCommunication] = useState<Communication | null>(null);

    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search, commitSearch]);

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef
    } = useViewportTableLimit(table, { rowHeight: COMMUNICATION_ROW_HEIGHT });

    // A status card reads back from the same `status`/`assignee` filters its
    // click writes to, so the card, the Status/Assignee pills and the query
    // params always agree.
    const isOpen = statusFilter[0] === 'open';
    const activeStat: CommunicationStat =
        isOpen && assigneeFilter[0] === ASSIGNEE_FILTER_UNASSIGNED
            ? 'unassigned'
            : isOpen && assigneeFilter[0] === ASSIGNEE_FILTER_ME
              ? 'mine'
              : isOpen && assigneeFilter.length === 0
                ? 'open'
                : 'all';

    const { page, limit, sort, order } = table.params;
    const sharedParams = {
        search: table.search || undefined,
        status: statusFilter[0] as CommunicationStatus | undefined,
        channel: channelFilter[0] as CommunicationChannel | undefined,
        category: categoryFilter ?? undefined,
        practice: practiceFilter || undefined,
        assignee: apiAssignees[0],
        sort,
        order
    };
    const paged = useCommunications({
        ...sharedParams,
        page,
        limit,
        enabled: !isInfinite && pageSizeReady
    });
    const infinite = useInfiniteCommunications({ ...sharedParams, limit, enabled: isInfinite });

    const communications = isInfinite ? infinite.communications : paged.communications;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isError = isInfinite ? infinite.isError : paged.isError;
    const error = isInfinite ? infinite.error : paged.error;
    // Invalidates by the shared `COMMUNICATIONS_KEY` prefix, so it refreshes
    // whichever of the two queries is currently active regardless of which
    // issued it.
    const { refresh } = paged;

    const { summary } = useCommunicationsSummary();

    useEffect(() => {
        if (isError) showToast(apiErrorMessage(error, 'Failed to load communications'), 'error');
    }, [isError, error, showToast]);

    useEffect(() => {
        setTitle('Communications');
        setSubtitle(`${meta.total} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [meta.total, setTitle, setSubtitle]);

    const deleteCommunication = useDeleteCommunication();

    const totalPages = Math.max(1, Math.ceil(meta.total / limit));

    const hasFilters =
        statusFilter.length > 0 ||
        channelFilter.length > 0 ||
        categoryFilter !== null ||
        practiceFilter !== ANY_PRACTICE ||
        assigneeFilter.length > 0;

    const clearFilters = () => {
        setSearch('');
        table.setFilter('status', []);
        table.setFilter('channel', []);
        table.setFilter('category', null);
        table.setFilter('practice', ANY_PRACTICE);
        table.setFilter('assignee', []);
    };

    // Clicking the active card again clears back to the unfiltered list.
    const handleStatChange = (stat: CommunicationStat) => {
        const next = stat === activeStat ? 'all' : stat;
        table.setFilter('status', next === 'all' ? [] : ['open']);
        table.setFilter(
            'assignee',
            next === 'mine'
                ? [ASSIGNEE_FILTER_ME]
                : next === 'unassigned'
                  ? [ASSIGNEE_FILTER_UNASSIGNED]
                  : []
        );
    };

    const goToNew = () => navigate('/accounts/communications/new');

    const goToCommunication = (communication: Communication) => {
        const path = communication.practiceId
            ? `/accounts/practices/${communication.practiceId}/communications/${communication.id}`
            : `/accounts/communications/${communication.id}`;
        navigate(path);
    };

    // No "Any practice" entry - re-picking the selected pill option already
    // means any (the shared dropdown's single-select clears on re-pick), and
    // offering both would give the same state two names.
    const practiceOptions = useMemo(
        () => [
            { value: UNASSIGNED_PRACTICE_FILTER, label: 'No practice yet' },
            ...practiceOptionsFromList(practices)
        ],
        [practices]
    );

    const filters: DataTableFilter[] = [
        {
            key: 'practice',
            label: 'Practice',
            options: practiceOptions,
            value: practiceFilter || null,
            onChange: (value) => table.setFilter('practice', value ?? ANY_PRACTICE),
            align: 'center',
            searchable: true
        },
        {
            key: 'assignee',
            label: 'Assignee',
            options: assigneeOptions,
            value: assigneeFilter[0] ?? null,
            onChange: (value) => table.setFilter('assignee', value ? [value] : []),
            align: 'center',
            searchable: true
        }
    ];

    // "Log a communication" ahead of the expand toggle - CommunicationsTable
    // appends the toggle itself, after this array, so it always lands
    // last/far right.
    const toolbarActions: DataTableButtonAction[] = [
        {
            key: 'log-communication',
            label: 'Log a communication',
            icon: <ChatBubbleLeftRightIcon className="h-4 w-4" />,
            variant: 'primary',
            onClick: goToNew
        }
    ];

    const handleConfirmDelete = () => {
        if (!deletingCommunication) return;
        deleteCommunication.mutate(
            { id: deletingCommunication.id, practiceId: deletingCommunication.practiceId },
            {
                onSuccess: () => {
                    showToast('Communication deleted', 'success');
                    setDeletingCommunication(null);
                },
                onError: (err) =>
                    showToast(apiErrorMessage(err, 'Failed to delete communication'), 'error')
            }
        );
    };

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <div className="px-8 pt-4 shrink-0">
                <CommunicationsStatCards
                    summary={summary}
                    activeStat={activeStat}
                    onChange={handleStatChange}
                />
            </div>

            <div className="px-8 flex-1 min-h-0 flex flex-col">
                <CommunicationsTable
                    communications={communications}
                    isLoading={isLoading}
                    isError={isError}
                    search={table.search}
                    hasFilters={hasFilters}
                    onClearFilters={clearFilters}
                    onRetry={() => void refresh()}
                    onLogCommunication={goToNew}
                    onOpenCommunication={goToCommunication}
                    onDelete={setDeletingCommunication}
                    showPractice
                    mode={table.mode}
                    onModeChange={table.setMode}
                    sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                    pagination={{ page, totalPages, total: meta.total, onPage: table.setPage }}
                    onLoadMore={infinite.fetchNextPage}
                    hasMore={infinite.hasNextPage}
                    isLoadingMore={infinite.isFetchingNextPage}
                    tableCardRef={tableCardRef}
                    tableHeaderRef={tableHeaderRef}
                    tableBodyRef={tableBodyRef}
                    paginationRef={paginationRef}
                    searchConfig={{
                        value: search,
                        onChange: setSearch,
                        placeholder: 'Search communications'
                    }}
                    filters={filters}
                    toolbarSlot={
                        <CommunicationsMoreFiltersMenu
                            statusFilter={statusFilter}
                            onStatusFilterChange={(value) => table.setFilter('status', value)}
                            channelFilter={channelFilter}
                            onChannelFilterChange={(value) => table.setFilter('channel', value)}
                            categoryFilter={categoryFilter}
                            onCategoryFilterChange={(value) => table.setFilter('category', value)}
                        />
                    }
                    toolbarActions={toolbarActions}
                />
            </div>

            {deletingCommunication && (
                <DeleteCommunicationConfirmModal
                    communication={deletingCommunication}
                    isDeleting={deleteCommunication.isPending}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setDeletingCommunication(null)}
                />
            )}
        </div>
    );
}
